import {observer_mgl} from "mobx-graphlink";
import React from "react";
import {Button, CheckBox, Row, Select, Spinner, Text, TextInput} from "react-vcomponents";
import {InfoButton, TextPlus} from "web-vcore";
import {Curve_CatmullRom_Subtype_values, FbmNoiseLayer_Config, InterpolateConfig, NoiseSpace_values, PathTransform} from "../../../../../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {PathTerrainFeature} from "../../../../../../Terrain/Heights/IsolineTypes.js";
import {rngStream, rngStreamPath} from "../../../../../../Terrain/Heights/TerrainRNG.js";
import {GetMapObjects} from "../../../../../../Server/DB/mapObjects.js";
import {ResolveOriginPathForPath} from "../../../../../../Server/DB/mapObjects/@MapObject/ObjPath/ObjPathResolvers.js";
import {TransformUI_SharedProps} from "../PathEditorTabs/Transforms.js";
import {SpinnerForPercent} from "../../../../../../Utils/ReactComponents/SpinnerForPercent.js";
import {E} from "js-vextensions";

/** The interpolate-transform editor. As of 2026-08-02 the Catmull-Rom params
 *  (subtype / tension / output-segment-ratio / meters-per-output-segment) are flattened onto
 *  `InterpolateConfig` and always visible — CR is the only backbone. The fBm-noise lateral-
 *  displacement layer is a toggleable sub-section: when the "fBm noise layer" checkbox is on,
 *  `cfg.fbmNoise` is present and not `disabled`; unchecking sets `fbmNoise.disabled = true`
 *  (the layer's params are kept so toggling back on restores them). Mirrors the former inline
 *  transform-editor body in Paths_CompUI.tsx (extracted 2026-08-01 alongside ExtrudeUI.tsx). */
export const InterpolateUI = observer_mgl((props: {transform: PathTransform} & TransformUI_SharedProps)=>{
	const {transform, map, obj, path, Change} = props;
	const interp = transform.interpolate!;
	const fbm = interp?.fbmNoise;
	const fbmEnabled = fbm != null && fbm.disabled !== true;
	// Mutual-exclusion warning: both output density fields set at once.
	const bothDensitySet = (interp.metersPerOutputSegment != null && interp.metersPerOutputSegment > 0) && (interp.outputSegmentRatio != null && interp.outputSegmentRatio >= 1);

	// Resolved fBm display values (seed-derived when unset). Computed once for the read-only
	// display rows; matches the engine's seed convention.
	const originPathId = ResolveOriginPathForPath(path, obj)?.path.path_id ?? "";
	const usesWorldSeed = PathTerrainFeature.IsActive(path.terrainFeature);
	let worldSeed = 0;
	if (usesWorldSeed) {
		const allMapObjects = GetMapObjects(map.id) ?? [];
		for (const mo of allMapObjects) {
			for (const mod of mo.modifiers ?? []) {
				if (mod.enabled && mod.terrainDefinition) { worldSeed = mod.terrainDefinition.seed; break; }
			}
			if (worldSeed) break;
		}
	}
	const roughnessRng = usesWorldSeed ? rngStream(worldSeed, originPathId, "shapeRoughness") : rngStreamPath(originPathId, "shapeRoughness");
	const wavelengthRng = usesWorldSeed ? rngStream(worldSeed, originPathId, "shapeWavelength") : rngStreamPath(originPathId, "shapeWavelength");
	const ptsForDisplay = path.points;
	let cpSpacingSum = 0;
	for (let i = 0; i < ptsForDisplay.length; i++) {
		const a = ptsForDisplay[i], b = ptsForDisplay[(i + 1) % ptsForDisplay.length];
		cpSpacingSum += Math.sqrt((b[0] - a[0])**2 + (b[1] - a[1])**2);
	}
	const meanCPSpacing = cpSpacingSum / Math.max(ptsForDisplay.length, 1);
	const fbmCfg = fbm ?? {};
	const resolvedOctaves = fbmCfg.octaves ?? 4;
	const resolvedPersistence = fbmCfg.persistence ?? 0.5;
	const resolvedRoughness = fbmCfg.roughness ?? (0.05 + roughnessRng() * 0.15);
	const resolvedWavelength = fbmCfg.wavelength ?? (meanCPSpacing * (0.8 + wavelengthRng() * 0.5));
	const resolvedControlPointBlendBand = fbmCfg.controlPointBlendBand ?? 0.2;
	const resolvedNoiseSpace = fbmCfg.noiseSpace ?? "local";

	return (
		<>
			{/* ── Catmull-Rom backbone params (always visible — CR is the only backbone) ── */}
			<Row mt={5}>
				<TextPlus info={`
					Subtype determines how the Catmull-Rom curve is calculated.
					* Centripetal: Good for most cases, avoids self-intersections.
					* Chordal: Similar to centripetal, but with different tension handling.
					* Catmull-Rom: The classic Catmull-Rom curve, can produce self-intersections if points are too close together.
				`.AsMultiline(0)}>Subtype:</TextPlus>
				<Select ml={5} options={Curve_CatmullRom_Subtype_values} value={interp.subtype ?? "centripetal"} onChange={val=>Change(t=>{ t.interpolate ??= new InterpolateConfig({}); t.interpolate!.subtype = val; })}/>
				{(interp.subtype ?? "centripetal") == "catmullrom" && <>
					<Text ml={5}>Tension:</Text>
					<Spinner ml={5} min={0} step={0.1} value={interp.tension ?? 0.5} onChange={val=>Change(t=>{ t.interpolate ??= new InterpolateConfig({}); t.interpolate!.tension = val; })}/>
				</>}
			</Row>
			<Row mt={5}>
				<TextPlus style={E(bothDensitySet && {textDecoration: "line-through"})} info={`
					How many points to generate per control-point segment (the legacy renderer behaviour).
					Mutually exclusive with meters-per-output-segment; when both are set, meters-per-output-segment wins and this one is ignored (the engine warns in the UI below).
					Clear this field (set to 0/undefined) and set Meters-per-output-segment to switch to meter-based sampling.
					Meter-based sampling is recommended when the fBm noise layer is enabled (uniform spacing prevents V-shapes at control points).
				`.AsMultiline(0)}>Output segment ratio:</TextPlus>
				<Spinner ml={5} min={0} step={1} value={interp.outputSegmentRatio} onChange={val=>Change(t=>{ t.interpolate ??= new InterpolateConfig({}); t.interpolate!.outputSegmentRatio = val; })}/>
				<Button mdIcon="delete" ml={5} title="Clear (fall back to meters-per-output-segment, or default 10)" onClick={()=>Change(t=>{ if (t.interpolate) t.interpolate.outputSegmentRatio = undefined; })}/>
			</Row>
			<Row mt={5}>
				<TextPlus info={`
					When set (> 0), re-sample the Catmull-Rom backbone at uniform arc-length intervals of this many metres.
					Recommended when fBm noise layer is enabled; it keeps the noise normal/offset baseline constant everywhere.
					(this prevents the V-at-control-point artefact that fixed output-segment-ratio produces under noise)
					Mutually exclusive with output-segment-ratio; when both are set, this one wins.
				`.AsMultiline(0)}>Meters per output segment:</TextPlus>
				<Spinner ml={5} min={0} step={1} value={interp.metersPerOutputSegment} onChange={val=>Change(t=>{ t.interpolate ??= new InterpolateConfig({}); t.interpolate!.metersPerOutputSegment = val; })}/>
				<Button mdIcon="delete" ml={5} title="Clear (fall back to output-segment-ratio)" onClick={()=>Change(t=>{ if (t.interpolate) t.interpolate.metersPerOutputSegment = undefined; })}/>
			</Row>

			{/* === FBM noise layer === */}
			<Row mt={10} center>
				<Text style={{fontWeight: "bold"}}>fBm noise layer:</Text>
				<InfoButton ml={5} text={`
					The interpolate transform replaces the path's control points with a denser Catmull-Rom curve tessellation, before any extrude transforms that follow it.
					The fBm noise layer (optional) displaces each tessellated point perpendicular to its tangent by multi-octave fBm noise — more organic, less geometrically-perfect. Good for natural isolines / coastlines.
					(fBm uses the path's pathId as a deterministic seed so the shape is stable across reloads; for terrain-isolines the world-seed also folds in.)
				`.AsMultiline(0)}/>
			</Row>
			<Row mt={5}>
				<CheckBox text="Enabled" value={fbmEnabled} onChange={val=>Change(t=>{
					t.interpolate ??= new InterpolateConfig({});
					if (val) {
						// Enable: ensure the layer exists and is not disabled.
						t.interpolate!.fbmNoise ??= new FbmNoiseLayer_Config();
						t.interpolate!.fbmNoise!.disabled = undefined;
					} else if (t.interpolate!.fbmNoise != null) {
						// Disable: keep the layer object (so params persist) but mark it inert.
						t.interpolate!.fbmNoise!.disabled = true;
					} else {
						// No layer yet and user unchecked — create one already-disabled, so the
						// params the user is about to edit are stored on a stable object.
						t.interpolate!.fbmNoise = new FbmNoiseLayer_Config({disabled: true});
					}
				})}/>
				<Button mdIcon="delete" ml={5} enabled={fbm != null}
					title="Remove the entire fBm noise substructure (clears all fBm params)"
					onClick={()=>Change(t=>{ if (t.interpolate) t.interpolate.fbmNoise = undefined; })}/>
			</Row>

			{fbm && <>
				<Row mt={5}>
					<TextPlus info={`
						What coordinate space the fBm noise is sampled in.
						* curve: noise parameterised by arc-position within each segment — per-segment independent seeding (drag-stable), but produces a character discontinuity at control points (the Blend-band field mitigates this) and a V-shape at CPs on sharp turns.
						* local: noise sampled at the backbone point's local coordinates (raw path.points x/y). A single continuous 2D field — no V at CPs, no CP-clustering. 100% stable for a given (x,y) in the data; translating the object carries the shape. Default.
						* world: noise sampled at world coordinates (local + the object's world position). The curve is a window into a global noise field; moving the object re-samples the field (bumps change). Physically natural for coastlines derived from terrain noise.
					`.AsMultiline(0)}>Noise space:</TextPlus>
					<Select ml={5} options={NoiseSpace_values.map(v=>({name: v, value: v}))} value={resolvedNoiseSpace}
						onChange={val=>Change(t=>{ t.interpolate!.fbmNoise ??= new FbmNoiseLayer_Config(); t.interpolate!.fbmNoise!.noiseSpace = val; })}/>
				</Row>
				<Row mt={5} style={{opacity: 0.6, fontSize: 11}}>
					<Text>Resolved FBM values (seed-derived when unset/"-1"):</Text>
				</Row>
				<Row mt={3} style={{opacity: 0.6, fontSize: 11}}>
					<Text style={{minWidth: 80}}>Octaves:</Text>
					<TextInput editable={false} style={{flex: 1}} value={String(resolvedOctaves)}/>
				</Row>
				<Row mt={3} style={{opacity: 0.6, fontSize: 11}}>
					<Text style={{minWidth: 80}}>Persistence:</Text>
					<TextInput editable={false} style={{flex: 1}} value={resolvedPersistence.toFixed(2)}/>
				</Row>
				<Row mt={3} style={{opacity: 0.6, fontSize: 11}}>
					<TextPlus style={{minWidth: 80}} info={`
						Lateral noise amplitude as a fraction of the curve's mean radius.
						NOTE: If you see "V" shapes at control points that are way sharper than bumps elsewhere, that is likely due to...
						   a too-large wavelength (combined with the envelope/forced-zeroing right around control points).
						   To fix this, adjust wavelength lower (and lower roughness in tandem); this will lessen the envelopes' divergence.
					`}>Roughness:</TextPlus>
					<TextInput editable={false} style={{flex: 1}} value={resolvedRoughness.toFixed(4)}/>
				</Row>
				<Row mt={3} style={{opacity: 0.6, fontSize: 11}}>
					<Text style={{minWidth: 80}}>Wavelength:</Text>
					<TextInput editable={false} style={{flex: 1}} value={resolvedWavelength.toFixed(1) + " m"}/>
				</Row>
				<Row mt={3} style={{opacity: 0.6, fontSize: 11}}>
					<Text style={{minWidth: 80}}>Blend-band:</Text>
					<TextInput editable={false} style={{flex: 1}} value={resolvedControlPointBlendBand.toFixed(2)}/>
				</Row>
				<Row mt={3} style={{opacity: 0.6, fontSize: 11}}>
					<Text style={{minWidth: 80}}>Noise space:</Text>
					<TextInput editable={false} style={{flex: 1}} value={resolvedNoiseSpace}/>
				</Row>
				<Row mt={5}>
					<TextPlus info={`
						Number of fBm octaves. Each octave doubles the frequency and scales its amplitude by persistence.
						More octaves = more fine detail, but also more computation. Typical: 3-5. Default: 4.
					`.AsMultiline(0)}>Octaves:</TextPlus>
					<Spinner ml={5} min={1} max={8} step={1} value={fbm?.octaves} onChange={val=>Change(t=>{ t.interpolate!.fbmNoise ??= new FbmNoiseLayer_Config(); t.interpolate!.fbmNoise!.octaves = val; })}/>
					<Button mdIcon="delete" ml={5} title="Clear (use engine-resolved default)" onClick={()=>Change(t=>{ if (t.interpolate?.fbmNoise) t.interpolate.fbmNoise.octaves = undefined; })}/>
				</Row>
				<Row mt={5}>
					<TextPlus info={`
						Per-octave amplitude multiplier (0-1). Each successive octave contributes persistence × the amplitude of the previous.
						0.5 (default): standard fBm — detail fades quickly.
						Near 1: all octaves equally strong — very rough, fractal appearance.
						Near 0: only the broadest octave is visible — very subtle undulation.
					`.AsMultiline(0)}>Persistence:</TextPlus>
					<Spinner ml={5} min={0.01} max={1} step={0.05} value={fbm?.persistence} onChange={val=>Change(t=>{ t.interpolate!.fbmNoise ??= new FbmNoiseLayer_Config(); t.interpolate!.fbmNoise!.persistence = val; })}/>
					<Button mdIcon="delete" ml={5} title="Clear (use engine-resolved default)" onClick={()=>Change(t=>{ if (t.interpolate?.fbmNoise) t.interpolate.fbmNoise.persistence = undefined; })}/>
				</Row>
				<Row mt={5}>
					<TextPlus info={`
						Lateral noise amplitude as a fraction of the curve's mean radius.
						0.1 (default): subtle organic variation.
						0.3+: pronounced wobble / deviations from the backbone Catmull-Rom curve.
						When unset (or 0), engine resolves to uniform[0.05, 0.2] seeded by the path's path_id.
					`.AsMultiline(0)}>Roughness:</TextPlus>
					<SpinnerForPercent ml={5} min={0} value={fbm?.roughness} onChange={val=>Change(t=>{ t.interpolate!.fbmNoise ??= new FbmNoiseLayer_Config(); t.interpolate!.fbmNoise!.roughness = val; })}/>
					<Button mdIcon="delete" ml={5} title="Clear (use seed-resolved value)" onClick={()=>Change(t=>{ if (t.interpolate?.fbmNoise) t.interpolate.fbmNoise.roughness = undefined; })}/>
				</Row>
				<Row mt={5}>
					<TextPlus info={`
						World-space wavelength of the coarsest fBm octave, in meters.
						Smaller values = more frequent undulations along the curve.
						When unset (0), engine resolves to meanControlPointSpacing × uniform[0.8, 1.3] — i.e. about one full noise cycle between each pair of control points.
						Set manually if you want explicit control over the undulation frequency.
					`.AsMultiline(0)}>Wavelength (m):</TextPlus>
					<Spinner ml={5} min={0} step={10} value={fbm?.wavelength} onChange={val=>Change(t=>{ t.interpolate!.fbmNoise ??= new FbmNoiseLayer_Config(); t.interpolate!.fbmNoise!.wavelength = val; })}/>
					<Button mdIcon="delete" ml={5} title="Clear (use seed-resolved value)" onClick={()=>Change(t=>{ if (t.interpolate?.fbmNoise) t.interpolate.fbmNoise.wavelength = undefined; })}/>
				</Row>
				{resolvedNoiseSpace === "curve" &&
				<Row mt={5}>
					<TextPlus info={`
						Width of the cross-fade band at each control point, as a fraction of segment length (0–0.5).
						Within this band the bump noise blends smoothly from the previous segment’s field into this segment’s, removing the incoherent double-bump / kink that per-segment noise otherwise produces at control points.
						0 = disabled (pure per-segment noise, bumps are discontinuous across CPs).
						0.2 (default): smooth handoff; matches the envelope ramp width so no new visible band appears.
						Higher (up to 0.5): wider, gentler handoff; more of each segment shows a mix of the two neighbours’ noise.
					`.AsMultiline(0)}>Blend-band:</TextPlus>
					<Spinner ml={5} min={0} max={0.5} step={0.05} value={fbm?.controlPointBlendBand} onChange={val=>Change(t=>{ t.interpolate!.fbmNoise ??= new FbmNoiseLayer_Config(); t.interpolate!.fbmNoise!.controlPointBlendBand = val; })}/>
					<Button mdIcon="delete" ml={5} title="Clear (use default 0.2)" onClick={()=>Change(t=>{ if (t.interpolate?.fbmNoise) t.interpolate.fbmNoise.controlPointBlendBand = undefined; })}/>
				</Row>}
			</>}
		</>
	);
});
